// Wheel de Noxara: a roda do Tibia (36 quadrados do centro para fora) comprada com gold, com bônus permanentes por cor.
// Verde: vida · Azul: mana · Vermelho: dano PvE · Roxo: gold dropado. Cada cor completa libera um bônus maior.
// Visual "Roda do Destino": aro dourado, quatro cores dos domínios e vidro escuro. CSS em client/css-parts/wheel.css
// (prefixo .wd-; o .wh- já é usado pelas classes internas do SVG em styles.css).
import { useState } from 'preact/hooks';
import { GEM_NAMES, GEM_REVEAL_GOLD, GEM_VESSELS, type GemState } from '../../../../shared/src/game/gems';
import { describeAffix } from '../../../../shared/src/game/affixes';
import {
  DOMAIN_NAMES, REVELATION_DESC, WHEEL_DOMAINS, WHEEL_PERK, WHEEL_REVELATION, WHEEL_SLOTS,
  canUseSlot, slotById, squaresDone, wheelBonus, wheelClean, wheelClickPrice, wheelCostToFinish, wheelPoints, wheelUsed,
  type Voc, type WheelDomain, type WheelSlot, type WheelState,
} from '../../../../shared/src/game/wheel';

const COLORS: Record<string, string> = { green: '#2fbf71', red: '#e5484d', blue: '#3b82f6', purple: '#a855f7' };
const DEEP: Record<string, string> = { green: '#0d3b24', red: '#3f1012', blue: '#0f2547', purple: '#2e1247' };
const n = (v: number) => v.toLocaleString('pt-BR');
const gp = (v: number) => v >= 1e6 ? `${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi` : v >= 1e4 ? `${Math.round(v / 1e3)} mil` : n(v);
type GemOp = 'reveal' | 'socket' | 'unsocket' | 'destroy';
const PERK_EMOJI: Record<WheelDomain, string> = { green: '❤', blue: '💧', red: '⚔', purple: '🪙' };

export function Wheel(props: { state: WheelState; vocation: string; bank: number; onBuy: (id: string, n: number) => void; gems?: GemState; onGem: (op: GemOp, id: number) => void }) {
  const [tab, setTab] = useState<'wheel' | 'gems'>('wheel');
  const st = wheelClean(props.state);
  const total = wheelPoints(), used = wheelUsed(st);
  // passo a passo abre sozinho enquanto o jogador ainda não comprou nenhum clique
  const [help, setHelp] = useState(used === 0);
  const voc = (props.vocation in WHEEL_REVELATION ? props.vocation : 'knight') as Voc;
  const b = wheelBonus(st, voc);
  const pct = total ? Math.round(used / total * 100) : 0;
  const price = wheelClickPrice(st);
  const toFinish = wheelCostToFinish(st);
  const sqTotal = WHEEL_DOMAINS.reduce((a, d) => a + b.squares[d], 0);
  const gemCount = props.gems?.list.length ?? 0;
  const hidden = props.gems?.list.filter((g) => !g.mods).length ?? 0;
  // anel do emblema: cada cor ocupa um quarto, preenchido conforme os quadrados completos
  const conic = WHEEL_DOMAINS.map((d, i) => {
    const a0 = i * 90, a1 = a0 + 90 * (b.squares[d] / 9);
    return `${COLORS[d]} ${a0}deg ${a1}deg, #1a1d26 ${a1}deg ${a0 + 90}deg`;
  }).join(', ');
  return (
    <div class={'wd' + (used >= total ? ' wd-done' : '')}>
      <div class="wd-head">
        <div class="wd-emblem" style={{ background: `conic-gradient(from -45deg, ${conic})` }} title={`${sqTotal} de 36 quadrados completos`}>
          <span><b>{pct}%</b><small>{sqTotal}/36</small></span>
        </div>
        <div class="wd-head-txt">
          <div class="wd-title">Roda do Destino
            <button class="wd-help" onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="wd-sub">Invista gold nos quadrados e ganhe vida, mana, dano e gold para sempre.</div>
          <div class="wd-bar" title={`${n(used)} de ${n(total)} cliques`}>
            <i style={{ width: `${pct}%` }} />
            <span>{used >= total ? 'Roda completa!' : `${n(used)} / ${n(total)} cliques · faltam ${gp(toFinish)} gp`}</span>
          </div>
        </div>
      </div>

      {help && (
        <div class="wd-steps">
          <div><i>🎯</i><b>1. Escolha um quadrado</b><small>Clique num quadrado da roda. Os do centro abrem primeiro; os outros pedem um vizinho completo.</small></div>
          <div><i>💰</i><b>2. Compre cliques</b><small>Cada clique custa gold do banco. O preço sobe 10% a cada quadrado completo.</small></div>
          <div><i>✨</i><b>3. Complete as cores</b><small>Cada quadrado dá +1% do bônus da cor; a cor inteira libera um bônus maior e 3 quadrados abrem um vessel de gema.</small></div>
        </div>
      )}

      <div class="wd-stats">
        <div class="wd-gold"><small>Banco</small><b>{gp(props.bank)}</b><span>gp disponíveis</span></div>
        <div><small>Próximo clique</small><b>{gp(price)}</b><span>gp por clique</span></div>
        <div><small>Quadrados</small><b>{sqTotal}<em>/36</em></b><span>{WHEEL_DOMAINS.filter((d) => b.complete[d]).length} cor(es) completa(s)</span></div>
        <div><small>Gemas</small><b>{gemCount}</b><span>{hidden ? `${hidden} para revelar` : 'todas reveladas'}</span></div>
      </div>

      <div class="wd-tabs">
        <button class={'wd-tab' + (tab === 'wheel' ? ' on' : '')} onClick={() => setTab('wheel')}>☸ Roda</button>
        <button class={'wd-tab' + (tab === 'gems' ? ' on' : '')} onClick={() => setTab('gems')}>💎 Gemas ({gemCount}){hidden ? <i class="wd-dot" title="Gemas por revelar" /> : null}</button>
      </div>
      {tab === 'wheel' ? <Slices st={st} voc={voc} bank={props.bank} onBuy={props.onBuy} />
        : <Gems gems={props.gems} vessels={b.vessels} bank={props.bank} onGem={props.onGem} />}
    </div>
  );
}

// ---- desenho: 5 anéis (50 junto do centro, depois 75, 100, 150 e 200 na borda), um quadrante por cor ----------------
const RING_OF: Record<number, number> = { 50: 0, 75: 1, 100: 2, 150: 3, 200: 4 };
const RADII = [58, 96, 134, 170, 204, 236];
const SIZE = 520, C = SIZE / 2;
interface Arc { sl: WheelSlot; r0: number; r1: number; a0: number; a1: number }
const ARCS: Arc[] = (() => {
  const groups = new Map<string, Array<{ sl: WheelSlot; ang: number; base: number }>>();
  for (const sl of WHEEL_SLOTS) {
    let ang = Math.atan2(sl.y + 0.5 - 3, sl.x + 0.5 - 3);
    if (ang < 0) ang += Math.PI * 2;
    const base = Math.floor(ang / (Math.PI / 2)) * (Math.PI / 2);
    const k = `${base}:${RING_OF[sl.cap]}`;
    const g = groups.get(k) ?? []; g.push({ sl, ang, base }); groups.set(k, g);
  }
  const out: Arc[] = [];
  for (const [k, g] of groups) {
    const ring = Number(k.split(':')[1]);
    g.sort((a, b) => a.ang - b.ang);
    const step = (Math.PI / 2) / g.length;
    g.forEach((e, i) => out.push({ sl: e.sl, r0: RADII[ring]! + 1.5, r1: RADII[ring + 1]! - 1.5, a0: e.base + i * step + 0.018, a1: e.base + (i + 1) * step - 0.018 }));
  }
  return out;
})();
const QUAD_MID: Record<string, number> = (() => {
  const o: Record<string, number> = {};
  for (const d of WHEEL_DOMAINS) { const a = ARCS.find((x) => x.sl.domain === d && x.sl.cap === 200)!; o[d] = (a.a0 + a.a1) / 2; }
  return o;
})();
const pt = (r: number, a: number) => `${(C + r * Math.cos(a)).toFixed(2)} ${(C + r * Math.sin(a)).toFixed(2)}`;
const arcPath = (r0: number, r1: number, a0: number, a1: number) =>
  `M${pt(r0, a0)} L${pt(r1, a0)} A${r1} ${r1} 0 0 1 ${pt(r1, a1)} L${pt(r0, a1)} A${r0} ${r0} 0 0 0 ${pt(r0, a0)}Z`;

/** ícones vetoriais dos bônus (coração, gota, espada, moeda) centrados em 0,0 */
function PerkIcon(props: { d: WheelDomain; x: number; y: number; s?: number; fill?: string }) {
  const k = (props.s ?? 1);
  const f = props.fill ?? '#fff';
  const path = props.d === 'green' ? 'M0 6 C-9 -1 -7 -9 -2.5 -8 C-1 -7.7 0 -6.5 0 -5.5 C0 -6.5 1 -7.7 2.5 -8 C7 -9 9 -1 0 6Z'
    : props.d === 'blue' ? 'M0 -9 C4 -3 6 0 6 3 A6 6 0 0 1 -6 3 C-6 0 -4 -3 0 -9Z'
    : props.d === 'red' ? 'M6 -8 L8 -6 L-2 4 L0 6 L-2 8 L-4 6 L-7 9 L-9 7 L-6 4 L-8 2 L-6 0 L-4 2 Z'
    : 'M0 -8 A8 8 0 1 1 0 8 A8 8 0 1 1 0 -8Z M0 -5 A5 5 0 1 0 0 5 A5 5 0 1 0 0 -5Z';
  return <path d={path} fill={f} fill-rule="evenodd" transform={`translate(${props.x} ${props.y}) scale(${k})`} style={{ pointerEvents: 'none' }} />;
}

function WheelSvg(props: { s: WheelState; sel: string; onSel: (id: string) => void; price: number }) {
  const { s } = props;
  const sq = squaresDone(s);
  return (
    <svg class="wheel-svg nx" viewBox={`0 0 ${SIZE} ${SIZE}`} width={460} height={460}>
      <defs>
        <radialGradient id="nx-bg"><stop offset="0%" stop-color="#1d2230" /><stop offset="70%" stop-color="#11141c" /><stop offset="100%" stop-color="#07080c" /></radialGradient>
        <radialGradient id="nx-hub"><stop offset="0%" stop-color="#3a2f12" /><stop offset="100%" stop-color="#120e05" /></radialGradient>
        {WHEEL_DOMAINS.map((d) => (
          <radialGradient key={d} id={'nx-' + d} cx={C} cy={C} r={RADII[5]} gradientUnits="userSpaceOnUse">
            <stop offset="20%" stop-color={COLORS[d]} stop-opacity="0.75" /><stop offset="100%" stop-color={COLORS[d]} />
          </radialGradient>
        ))}
        <filter id="nx-glow" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      <circle cx={C} cy={C} r={RADII[5]! + 18} fill="url(#nx-bg)" />
      {/* aro dourado com marcações, lembrando a roda do Tibia */}
      <circle cx={C} cy={C} r={RADII[5]! + 14} fill="none" stroke="#c9a227" stroke-width="2" opacity="0.8" />
      <circle cx={C} cy={C} r={RADII[5]! + 6} fill="none" stroke="#c9a22755" stroke-width="1" />
      {Array.from({ length: 72 }, (_, i) => { const a = i * Math.PI / 36; return <line key={i} x1={C + (RADII[5]! + 7) * Math.cos(a)} y1={C + (RADII[5]! + 7) * Math.sin(a)} x2={C + (RADII[5]! + (i % 2 ? 10 : 13)) * Math.cos(a)} y2={C + (RADII[5]! + (i % 2 ? 10 : 13)) * Math.sin(a)} stroke="#c9a227" stroke-width="1" opacity="0.6" />; })}
      {/* fundo de cada quadrante na cor do bônus */}
      {WHEEL_DOMAINS.map((d) => { const m = QUAD_MID[d]!; return <path key={d} d={arcPath(RADII[0]!, RADII[5]!, m - Math.PI / 4 + 0.01, m + Math.PI / 4 - 0.01)} fill={DEEP[d]} opacity="0.55" />; })}
      {ARCS.map(({ sl, r0, r1, a0, a1 }) => {
        const v = s[sl.id] ?? 0, ok = canUseSlot(s, sl.id), f = v / sl.cap, full = f >= 1;
        const mid = (a0 + a1) / 2, rm = (r0 + r1) / 2;
        return (
          <g key={sl.id} data-id={sl.id} class={'wh-slot' + (ok || v ? '' : ' locked') + (props.sel === sl.id ? ' sel' : '') + (full ? ' full' : '')} onClick={() => props.onSel(sl.id)}>
            <title>{`${DOMAIN_NAMES[sl.domain]} · ${WHEEL_PERK[sl.domain].square} · ${v}/${sl.cap}`}</title>
            <path d={arcPath(r0, r1, a0, a1)} fill="#0b0d13" fill-opacity="0.85" stroke={props.sel === sl.id ? '#ffd700' : full ? COLORS[sl.domain] : '#2a3142'} stroke-width={props.sel === sl.id ? 2.5 : 1.2} />
            {f > 0 && <path d={arcPath(r0, r0 + (r1 - r0) * f, a0, a1)} fill={`url(#nx-${sl.domain})`} opacity={full ? 0.95 : 0.7} filter={full ? 'url(#nx-glow)' : undefined} style={{ pointerEvents: 'none' }} />}
            {full ? <PerkIcon d={sl.domain} x={C + rm * Math.cos(mid)} y={C + rm * Math.sin(mid)} s={0.85} />
              : <text x={C + rm * Math.cos(mid)} y={C + rm * Math.sin(mid) + 3} class="wh-pts">{Math.round(f * 100)}%</text>}
          </g>
        );
      })}
      {/* rótulos dos bônus por fora da roda */}
      {WHEEL_DOMAINS.map((d) => {
        const m = QUAD_MID[d]!, r = RADII[5]! + 2;
        const x = C + (r - 18) * Math.cos(m) * 1.32, y = C + (r - 18) * Math.sin(m) * 1.32;
        return (
          <g key={'lb' + d}>
            <circle cx={x} cy={y} r="22" fill="#0b0d13" stroke={COLORS[d]} stroke-width="2" filter="url(#nx-glow)" />
            <PerkIcon d={d} x={x} y={y - 3} s={1.05} fill={COLORS[d]} />
            <text x={x} y={y + 15} class="wh-qd">{sq[d]}/9</text>
          </g>
        );
      })}
      {/* centro: preço do próximo clique */}
      <circle cx={C} cy={C} r={RADII[0]! - 3} fill="url(#nx-hub)" stroke="#c9a227" stroke-width="2" />
      <text x={C} y={C - 12} class="wh-center-s">clique</text>
      <text x={C} y={C + 6} class="wh-center">{gp(props.price)}</text>
      <text x={C} y={C + 20} class="wh-center-s">gp</text>
    </svg>
  );
}

function Slices(props: { st: WheelState; voc: Voc; bank: number; onBuy: (id: string, n: number) => void }) {
  const voc = props.voc;
  const s = props.st;
  const [sel, setSel] = useState<string>('GREEN_50');
  const b = wheelBonus(s, voc);
  const price = wheelClickPrice(s);
  const cur = slotById(sel)!;
  const v = s[sel] ?? 0, ok = canUseSlot(s, sel), left = cur.cap - v;
  const can = (k: number) => ok && left > 0 && props.bank >= price * Math.min(k, left);
  const fullDesc: Record<WheelDomain, string> = {
    green: `Gift of Life: ${REVELATION_DESC['Gift of Life']!(3)}`,
    blue: '+5% de mana permanente.',
    red: '+5% de vida permanente.',
    purple: `${WHEEL_REVELATION[voc].purple}: ${REVELATION_DESC[WHEEL_REVELATION[voc].purple]?.(3) ?? ''}`,
  };
  const state = left <= 0 ? 'full' : !ok && !v ? 'lock' : 'open';
  const col = COLORS[cur.domain]!;
  const needGold = left > 0 && ok && props.bank < price;
  return (
    <>
      <div class="wd-main">
        <div class="wd-stage">
          <WheelSvg s={s} sel={sel} onSel={setSel} price={price} />
          <small class="wd-legend"><i class="l-full" />completo <i class="l-open" />disponível <i class="l-lock" />bloqueado</small>
        </div>
        <div class="wd-side">
          <div class={'wd-card wd-' + state} style={{ '--wd-c': col } as Record<string, string>}>
            <div class="wd-card-h">
              <span class="wd-card-ico">{PERK_EMOJI[cur.domain]}</span>
              <div><b>Quadrado {DOMAIN_NAMES[cur.domain]}</b><small>{cur.cap} cliques · completo dá <b>{WHEEL_PERK[cur.domain].square}</b></small></div>
              <span class="wd-tag">{state === 'full' ? '✔ Completo' : state === 'lock' ? '🔒 Bloqueado' : 'Disponível'}</span>
            </div>
            <div class="wd-cbar"><i style={{ width: `${v / cur.cap * 100}%` }} /><span>{v} / {cur.cap}</span></div>
            {state === 'lock' ? <small class="wd-why">Complete um quadrado vizinho para abrir este (a roda abre do centro para fora).</small>
              : state === 'full' ? <small class="wd-ok">Bônus deste quadrado ativo para sempre.</small>
              : <div class="wd-price"><span>Clique <b>{n(price)} gp</b></span><span>Completar <b>{n(price * left)} gp</b></span></div>}
            {needGold && <small class="wd-why">Gold insuficiente no banco para um clique.</small>}
            <div class="wd-ctrl">
              <button class="tb-button" disabled={!can(1)} onClick={() => props.onBuy(sel, 1)}>+1</button>
              <button class="tb-button" disabled={!can(10)} onClick={() => props.onBuy(sel, 10)}>+10</button>
              <button class="tb-button green" disabled={!can(left)} onClick={() => props.onBuy(sel, left)}>Completar</button>
            </div>
          </div>
          <div class="wd-doms">
            {WHEEL_DOMAINS.map((d) => (
              <div key={d} class={'wd-dom' + (b.complete[d] ? ' done' : '')} style={{ '--wd-c': COLORS[d] } as Record<string, string>}>
                <div class="wd-dom-h"><span>{PERK_EMOJI[d]}</span><b>{DOMAIN_NAMES[d]}</b><em>+{b.squares[d]}%</em></div>
                <div class="wd-pips">{Array.from({ length: 9 }, (_, i) => <i key={i} class={i < b.squares[d] ? 'on' : ''} />)}</div>
                <small>{WHEEL_PERK[d].square} por quadrado · {b.squares[d]}/9</small>
                <small class={b.complete[d] ? 'wd-ok' : 'wd-dim'}>{b.complete[d] ? '★ Liberado' : '🔒 Cor completa'}: {fullDesc[d]}</small>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div class="wd-sum">
        <div class="wd-sec">O que a sua Wheel dá agora</div>
        <div class="wd-chips">
          <span style={{ '--wd-c': COLORS.green } as Record<string, string>}><b>+{b.hpPct}%</b> vida</span>
          <span style={{ '--wd-c': COLORS.blue } as Record<string, string>}><b>+{b.manaPct}%</b> mana</span>
          <span style={{ '--wd-c': COLORS.red } as Record<string, string>}><b>+{b.dmgPct}%</b> dano PvE</span>
          <span style={{ '--wd-c': COLORS.purple } as Record<string, string>}><b>+{b.goldPct}%</b> gold dropado</span>
          {b.complete.green && <span class="wd-rev">★ Gift of Life</span>}
          {b.complete.purple && <span class="wd-rev">★ {WHEEL_REVELATION[voc].purple}</span>}
        </div>
        <small class="wd-dim">O preço do clique começa em 5.000 gp e sobe 10% a cada quadrado completo. Os quadrados do centro abrem primeiro; os outros pedem um vizinho completo.
          Cada 3 quadrados completos de uma cor abrem um vessel de gema dessa cor.</small>
      </div>
    </>
  );
}

function Gems(props: { gems?: GemState; vessels: Record<string, number>; bank: number; onGem: (op: GemOp, id: number) => void }) {
  const list = [...(props.gems?.list ?? [])].sort((a, b) => b.size - a.size || a.id - b.id);
  const sockets = props.gems?.sockets ?? {};
  const [kill, setKill] = useState<number | null>(null);
  return (
    <div class="wd-gems">
      <div class="wd-info">
        <span>💎 Gemas caem de bosses (Lesser 20%, Regular 8%, Greater 2% por boss). Revele com gold e encaixe uma por domínio.</span>
        <span>Cada 3 quadrados completos de uma cor abrem 1 vessel dela; o encaixe pede Lesser {GEM_VESSELS[0]}, Regular {GEM_VESSELS[1]}, Greater {GEM_VESSELS[2]} vessel(s) (3, 6 ou 9 quadrados).
          Desfazer uma gema dá fragmentos de pedra de proteção. Banco: <b class="gold">{n(props.bank)} gp</b>.</span>
      </div>
      <div class="wd-sockets">
        {WHEEL_DOMAINS.map((d) => {
          const g = list.find((x) => x.id === sockets[d]);
          const ves = props.vessels[d] ?? 0;
          const off = g ? ves < GEM_VESSELS[g.size] : false;
          return (
            <div key={d} class={'wd-socket' + (g ? off ? ' off' : ' on' : '')} style={{ '--wd-c': COLORS[d] } as Record<string, string>}>
              <div class="wd-socket-h"><b>{DOMAIN_NAMES[d]}</b><span class="wd-ves" title={`${ves} vessel(s) aberto(s)`}>{[0, 1, 2].map((i) => <i key={i} class={i < ves ? 'on' : ''} />)}</span></div>
              <span class={'wd-gem-slot' + (g ? ' s' + g.size : '')}>{g ? <i /> : '+'}</span>
              {g ? <small><b>{GEM_NAMES[g.size]}</b><br />{g.mods!.map(describeAffix).join(', ')}{off ? <><br /><span class="wd-why">Inativa: faltam vessels</span></> : ''}</small>
                : <small class="wd-dim">Vazio</small>}
            </div>
          );
        })}
      </div>
      <div class="wd-gem-list">
        {list.map((g) => {
          const on = sockets[g.domain] === g.id;
          const ves = props.vessels[g.domain] ?? 0, needV = GEM_VESSELS[g.size];
          return (
            <div key={g.id} class={'wd-gem' + (on ? ' on' : '') + (!g.mods ? ' hidden' : '')} style={{ '--wd-c': COLORS[g.domain] } as Record<string, string>}>
              <span class={'wd-gem-ico s' + g.size}>{!g.mods && '?'}</span>
              <span class="wd-gem-txt"><b>{GEM_NAMES[g.size]}</b> <small>· {DOMAIN_NAMES[g.domain]}{on ? ' · encaixada' : ''}</small>
                <small>{g.mods ? g.mods.map(describeAffix).join(', ') : 'Não revelada'}</small>
                {g.mods && !on && ves < needV && <small class="wd-why">Precisa de {needV} vessel(s) {DOMAIN_NAMES[g.domain]} (tem {ves}).</small>}
              </span>
              <span class="wd-gem-act">
                {!g.mods && <button class="tb-button green" disabled={props.bank < GEM_REVEAL_GOLD[g.size]} onClick={() => props.onGem('reveal', g.id)}>Revelar ({gp(GEM_REVEAL_GOLD[g.size])} gp)</button>}
                {g.mods && !on && <button class="tb-button green" disabled={ves < needV} onClick={() => props.onGem('socket', g.id)}>Encaixar</button>}
                {on && <button class="tb-button" onClick={() => props.onGem('unsocket', g.id)}>Tirar</button>}
                {kill === g.id ? <>
                  <button class="tb-button wd-danger" onClick={() => { props.onGem('destroy', g.id); setKill(null); }}>Confirmar</button>
                  <button class="tb-button" onClick={() => setKill(null)}>Não</button>
                </> : <button class="tb-button" onClick={() => setKill(g.id)}>Desfazer</button>}
              </span>
            </div>
          );
        })}
        {!list.length && <div class="wd-empty"><span>💎</span><b>Nenhuma gema ainda</b><small>Derrote bosses para encontrar.</small></div>}
      </div>
    </div>
  );
}
